import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCheck, faCheckDouble } from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import { onSocket } from '../../utils/socket';
import Avatar from '../Avatar';
import { receiptTime, stamp } from '../../utils/receipts';

/**
 * Who a message of mine has reached, and who has read it.
 *
 * Opened from the message's own menu, and only ever for the sender — the server
 * refuses anybody else (GET /:id/messages/:messageId/info), for the reason given
 * there.
 *
 * Two shapes, because the question is different. In a direct chat there is one
 * reader, so it is a timeline: sent, delivered, seen. In a group it is a roster
 * split three ways — read, reached but unread, not reached yet — because "who
 * has not seen this" is what somebody opening this panel is almost always
 * trying to find out.
 *
 * It stays live while open: a receipt landing for this conversation refreshes
 * it, so watching the panel is watching people catch up.
 */

const Row = ({ person, detail }) => (
    <div className="msgr-info-person">
        <Avatar name={person.name} profilePic={person.profilePic} className="msgr-avatar" />
        <div className="msgr-info-person-text">
            <strong>{person.name}</strong>
            {detail && <span>{detail}</span>}
        </div>
    </div>
);

const Section = ({ title, icon, tone, people, detailFor }) => {
    if (!people.length) return null;
    return (
        <section className="msgr-info-section">
            <h5 className={tone}>
                <FontAwesomeIcon icon={icon} />
                {title}
                <span className="msgr-info-count">{people.length}</span>
            </h5>
            {people.map((r) => (
                <Row key={r.user._id} person={r.user} detail={detailFor(r)} />
            ))}
        </section>
    );
};

/*
 * `infoUrl`, `threadId` and `receiptEvents` let a thread that is not a
 * conversation - a task discussion - use the same panel: where to ask, and
 * which pushes mean its receipts moved.
 */
const CHAT_RECEIPT_EVENTS = ['message:read', 'message:delivered'];

const MessageInfo = ({
    conversationId, message, isGroup, onClose,
    infoUrl, threadId, receiptEvents = CHAT_RECEIPT_EVENTS
}) => {
    const url = infoUrl || `/conversations/${conversationId}/messages/${message._id}/info`;
    const watchedId = String(threadId || conversationId);
    const [info, setInfo] = useState(null);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        try {
            const res = await api.get(url);
            setInfo(res.data);
            setError('');
        } catch (err) {
            setError(err.response?.data?.message || 'Could not load message info.');
        }
    }, [url]);

    useEffect(() => { load(); }, [load]);

    // Receipts arrive in bursts — a whole group opening the thread at once — so
    // they are gathered for a moment rather than refetched one by one.
    useEffect(() => {
        let timer = null;
        const again = ({ conversationId: cid, taskId }) => {
            if (String(cid || taskId) !== watchedId) return;
            clearTimeout(timer);
            timer = setTimeout(load, 300);
        };
        const offs = receiptEvents.map((event) => onSocket(event, again));
        return () => {
            clearTimeout(timer);
            offs.forEach((off) => off());
        };
    }, [watchedId, receiptEvents, load]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const sentAt = info?.sentAt || message.createdAt;
    const recipients = info?.recipients || [];
    const preview = message.text || (message.attachments?.length ? 'Attachment' : '');

    let body;
    if (error) {
        body = <p className="msgr-empty">{error}</p>;
    } else if (!info) {
        body = <p className="msgr-empty">Loading…</p>;
    } else if (!isGroup) {
        const other = recipients[0];
        body = (
            <div className="msgr-info-timeline">
                <div className="msgr-info-step">
                    <FontAwesomeIcon icon={faCheck} className="msgr-info-icon" />
                    <span>Sent</span>
                    <time>{stamp(sentAt)}</time>
                </div>
                <div className="msgr-info-step">
                    <FontAwesomeIcon icon={faCheckDouble} className="msgr-info-icon" />
                    <span>Delivered</span>
                    <time>{receiptTime(other?.deliveredAt, sentAt) || 'Not yet'}</time>
                </div>
                <div className="msgr-info-step">
                    <FontAwesomeIcon icon={faCheckDouble} className="msgr-info-icon is-read" />
                    <span>Seen</span>
                    <time>{receiptTime(other?.readAt, sentAt) || 'Not yet'}</time>
                </div>
            </div>
        );
    } else {
        const read = recipients.filter((r) => r.readAt)
            .sort((a, b) => new Date(a.readAt) - new Date(b.readAt));
        const reached = recipients.filter((r) => r.deliveredAt && !r.readAt)
            .sort((a, b) => new Date(a.deliveredAt) - new Date(b.deliveredAt));
        const waiting = recipients.filter((r) => !r.deliveredAt)
            .sort((a, b) => a.user.name.localeCompare(b.user.name));

        body = recipients.length ? (
            <>
                <Section
                    title="Read by"
                    icon={faCheckDouble}
                    tone="is-read"
                    people={read}
                    detailFor={(r) => receiptTime(r.readAt, sentAt)}
                />
                <Section
                    title="Delivered to"
                    icon={faCheckDouble}
                    tone=""
                    people={reached}
                    detailFor={(r) => receiptTime(r.deliveredAt, sentAt)}
                />
                <Section
                    title="Not delivered yet"
                    icon={faCheck}
                    tone=""
                    people={waiting}
                    detailFor={() => null}
                />
            </>
        ) : (
            <p className="msgr-empty">Nobody else was in the group when this was sent.</p>
        );
    }

    return (
        <div className="msgr-modal-back" onClick={onClose}>
            <div
                className="msgr-modal msgr-info"
                role="dialog"
                aria-label="Message info"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="msgr-modal-head">
                    <h4>Message info</h4>
                    <button className="msgr-icon-btn" onClick={onClose} aria-label="Close">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div className="msgr-modal-body">
                    <div className="msgr-info-preview">
                        <p>{preview}</p>
                        <time>{stamp(sentAt)}</time>
                    </div>
                    {body}
                </div>
            </div>
        </div>
    );
};

export default MessageInfo;
